import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Action, NgxsAfterBootstrap, Selector, State, StateContext, Store } from '@ngxs/store';
import { LINK_ACCOUNT_ERROR_HANDLERS, LinkAccountState } from '@platform-frontend/account';
import {
  LicenseType,
  LicenseValidityChooser,
  LicenseValidityContext,
  MARKETPLACE_ERROR_HANDLERS,
  PaymentMethodRedirection,
  PricingInformation,
  ScaService,
  SerializedProductService,
  isReusablePaymentMethodIdentifier
} from '@platform-frontend/marketplace';
import { AdyenBrowserInfoService, CreditCardType, DeviceFingerprintService } from '@platform-frontend/payment-util';
import { PLATFORM_ENVIRONMENT } from '@platform-frontend/platform';
import {
  ClearNotifications,
  DownloadService,
  GotoError,
  InvalidStateError,
  RefreshWindow,
  ShowErrorNotification,
  handleErrors,
  tapNextOrError
} from '@platform-frontend/util';
import { Observable, OperatorFunction, Subject, combineLatest, of, pipe, throwError } from 'rxjs';
import { distinctUntilChanged, map, skip, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { FetchAssets } from '../asset/asset.actions';
import { FlowPreparationStarted } from '../flow/flow.actions';
import {
  MSG_CHECKOUT_PRICING_COUNTRY_INVALID,
  MSG_CHECKOUT_PRICING_DEFAULT,
  MSG_CHECKOUT_PRICING_INTERNAL_VAT_CALCULATION_ERROR,
  MSG_CHECKOUT_PRICING_INVALID_VAT_ID,
  MSG_CHECKOUT_PRICING_QUANTITY_CONSTRAINTS_VIOLATION,
  MSG_CHECKOUT_PRICING_VAT_SERVICE_NOT_REACHABLE,
  MSG_DOWNLOAD_LATEST_INVOICE_DEFAULT,
  MSG_PURCHASE_ALREADY_HAVE_ACTIVE_SUBSCRIPTION,
  MSG_PURCHASE_ALREADY_HAVE_CANCELED_SUBSCRIPTION,
  MSG_PURCHASE_OR_RENEW_AUTHORIZATION_REFUSED,
  MSG_PURCHASE_OR_RENEW_BILLING_INFORMATION_NOT_FOUND,
  MSG_PURCHASE_OR_RENEW_BILLING_INFORMATION_REQUIRED_MISSING,
  MSG_PURCHASE_OR_RENEW_BILLING_INFORMATION_REQUIRED_NOT_SELECTED,
  MSG_PURCHASE_OR_RENEW_CARD_EXPIRED,
  MSG_PURCHASE_OR_RENEW_COUNTRY_INVALID,
  MSG_PURCHASE_OR_RENEW_CVC_DECLINED,
  MSG_PURCHASE_OR_RENEW_DEFAULT,
  MSG_PURCHASE_OR_RENEW_ENCRYPTED_DATA_EXPIRED,
  MSG_PURCHASE_OR_RENEW_EXPIRY_DATE_INVALID,
  MSG_PURCHASE_OR_RENEW_FRAUD_DETECTED,
  MSG_PURCHASE_OR_RENEW_INTERNAL_VAT_CALCULATION_ERROR,
  MSG_PURCHASE_OR_RENEW_INVALID_CARD_NUMBER,
  MSG_PURCHASE_OR_RENEW_INVALID_ENCRYPTED_DATA,
  MSG_PURCHASE_OR_RENEW_INVALID_IBAN,
  MSG_PURCHASE_OR_RENEW_INVALID_VAT_ID,
  MSG_PURCHASE_OR_RENEW_METADATA_ALREADY_USED,
  MSG_PURCHASE_OR_RENEW_NOT_ENOUGH_BALANCE,
  MSG_PURCHASE_OR_RENEW_NOT_ENOUGH_BUDGET,
  MSG_PURCHASE_OR_RENEW_OUTDATED_BILLING_INFORMATION_VERSION,
  MSG_PURCHASE_OR_RENEW_OUTDATED_PAYMENT_INFORMATION_VERSION,
  MSG_PURCHASE_OR_RENEW_PAYMENT_METHOD_NOT_FOUND,
  MSG_PURCHASE_OR_RENEW_PAYMENT_SUCCESS_BUT_ERROR,
  MSG_PURCHASE_OR_RENEW_PAYPAL_PAYMENT_REFUSED,
  MSG_PURCHASE_OR_RENEW_QUANTITY_CONSTRAINTS_VIOLATION,
  MSG_PURCHASE_OR_RENEW_REDIRECT_ID_NOT_KNOWN,
  MSG_PURCHASE_OR_RENEW_REJECTED_PAYMENT,
  MSG_PURCHASE_OR_RENEW_SUBSCRIPTION_DEFAULT,
  MSG_PURCHASE_OR_RENEW_VAT_SERVICE_NOT_REACHABLE,
  MSG_PURCHASE_OR_RENEW_WRONG_PRICING,
  MSG_RENEW_ALREADY_HAVE_ACTIVE_SUBSCRIPTION,
  MSG_RENEW_SUBSCRIPTION_EXPIRED
} from '../notification-messages';
import { PaymentInformation, WalletPaymentDataService } from '../payment-wallet';
import { ProductState } from '../product';
import { LicenseValidityWarningService } from './license-validity-warning.service';
import { PurchaseOrRenewAddition } from './model/purchase-or-renew-addition';
import { PurchaseOrRenewParameters } from './model/purchase-or-renew-parameters';
import { ShoppingCartStateModel } from './model/shopping-cart-state-model';
import { PricingRelevantBillingInfos, ShoppingCartBillingState } from './shopping-cart-billing';
import {
  DownloadLatestInvoice,
  Purchase,
  PurchaseOrRenew,
  PurchaseOrRenewActionParams,
  RefreshPricing,
  Renew,
  RequestRefreshPricing,
  SelectLicenseType,
  SelectLicenseValidity
} from './shopping-cart.actions';

const DEFAULT_STATE: ShoppingCartStateModel = {
  selectedLicenseTypeId: null,
  pricingInformation: null,
  selectedQuantity: null,
  selectedLicenseValidity: null
};

@State<ShoppingCartStateModel>({
  name: 'shoppingCart',
  defaults: DEFAULT_STATE
})
@Injectable()
export class ShoppingCartState implements NgxsAfterBootstrap {
  constructor(
    private downloadService: DownloadService,
    private http: HttpClient,
    private serializedProductService: SerializedProductService,
    private scaService: ScaService,
    private store: Store,
    private walletPaymentDataService: WalletPaymentDataService,
    private deviceFingerprintService: DeviceFingerprintService,
    private readonly adyenBrowserInfoService: AdyenBrowserInfoService,
    private readonly licenseValidityWarningService: LicenseValidityWarningService
  ) {}

  private static readonly PRICING_TAG = Symbol('ShoppingCartState: PricingTag');

  pricingRelevantBillingInfos$: Observable<PricingRelevantBillingInfos> = this.store.select(ShoppingCartBillingState.pricingRelevantBillingInfos);
  commercialLicenseTypes$: Observable<LicenseType[]> = this.store.select(ProductState.commercialLicenseTypes);

  private refreshPricingRequested: Subject<void> = new Subject<void>();

  @Selector([ProductState.commercialLicenseTypes])
  static selectedLicenseType(state: ShoppingCartStateModel, commercialLicenseTypes: LicenseType[]): LicenseType | null {
    return commercialLicenseTypes.find(licenseType => licenseType.licenseTypeId === state.selectedLicenseTypeId) ?? null;
  }

  @Selector([ShoppingCartState.selectedLicenseType])
  static licenseTypeValidity(state: ShoppingCartStateModel, selectedLicenseType: LicenseType | null): LicenseValidityContext | null {
    return selectedLicenseType?.validityUpdate.context ?? null;
  }

  @Selector([ShoppingCartState.licenseTypeValidity])
  static selectedValidity(state: ShoppingCartStateModel, licenseTypeValidity: LicenseValidityContext | null): LicenseValidityContext | null {
    return state.selectedLicenseValidity ?? licenseTypeValidity;
  }

  @Selector([ShoppingCartState.selectedLicenseType])
  static validityChooser(state: ShoppingCartStateModel, selectedLicenseType: LicenseType | null): LicenseValidityChooser | null {
    return selectedLicenseType?.validityChooser ?? null;
  }

  @Selector([ShoppingCartState.validityChooser, ShoppingCartState.selectedValidity])
  static canShoppingCartBeConfigured(
    state: ShoppingCartStateModel,
    validityChooser: LicenseValidityChooser | null,
    selectedValidity: LicenseValidityContext | null
  ): boolean {
    return validityChooser !== null && selectedValidity !== null;
  }

  @Selector()
  static selectedLicenseTypeId(state: ShoppingCartStateModel): string | null {
    return state.selectedLicenseTypeId;
  }

  @Selector()
  static selectedQuantity(state: ShoppingCartStateModel): number | null {
    return state.selectedQuantity;
  }

  @Selector()
  static pricingInformation(state: ShoppingCartStateModel): PricingInformation | 'LOADING' | null {
    return state.pricingInformation;
  }

  @Selector([ShoppingCartState.pricingInformation])
  static isPricingInformationLoading(state: ShoppingCartStateModel, pricingInformation: PricingInformation | 'LOADING' | null): boolean {
    return pricingInformation === 'LOADING';
  }

  ngxsAfterBootstrap(): void {
    combineLatest([
      this.store.select(ShoppingCartState.selectedLicenseTypeId).pipe(distinctUntilChanged()),
      this.store.select(ShoppingCartState.selectedQuantity).pipe(distinctUntilChanged()),
      this.pricingRelevantBillingInfos$.pipe(
        skip(2),
        distinctUntilChanged((x, y) => x.countryCode === y.countryCode && x.billingInformationId === y.billingInformationId)
      )
    ])
      .pipe(
        // Use tap to keep dispatching, if RefreshPricing errors
        tap(([licenseTypeId, quantity, { countryCode, billingInformationId: billingAddressId }]) =>
          this.store.dispatch(new RefreshPricing(licenseTypeId, countryCode, billingAddressId, quantity))
        )
      )
      .subscribe();
  }

  @Action(FlowPreparationStarted)
  preselectLicenseTypeId(ctx: StateContext<ShoppingCartStateModel>, { flow }: FlowPreparationStarted): Observable<unknown> {
    if (flow.type !== 'COMMERCIAL_CHECKOUT') {
      ctx.patchState(DEFAULT_STATE);
      return of(null);
    }

    return this.commercialLicenseTypes$.pipe(
      take(1),
      map(commercialLicenseTypes => this.getSelectedLicenseType(commercialLicenseTypes)),
      tap(selectedLicenseType => ctx.patchState({ ...selectedLicenseType, selectedLicenseValidity: null })),
      this.checkLicenseValidity()
    );
  }

  private getSelectedLicenseType(licenseTypes: LicenseType[]): { selectedLicenseTypeId: string; selectedQuantity: number } {
    const licenseType = licenseTypes.find(licenseType => licenseType.quantityPreselection);

    if (!licenseType || !licenseType.quantityPreselection) {
      throw new InvalidStateError('LicenseType preselection failed.');
    }

    return { selectedLicenseTypeId: licenseType.licenseTypeId, selectedQuantity: licenseType.quantityPreselection };
  }

  @Action(SelectLicenseType)
  selectLicenseType(ctx: StateContext<ShoppingCartStateModel>, { licenseTypeId }: SelectLicenseType): Observable<unknown> {
    return this.commercialLicenseTypes$.pipe(
      take(1),
      switchMap(licenseTypes => this.getLicenseTypeIdForReference(licenseTypeId, licenseTypes)),
      tap(selectedLicenseTypeId => ctx.patchState({ selectedLicenseTypeId })),
      this.checkLicenseValidity()
    );
  }

  private getLicenseTypeIdForReference(reference: string, licenseTypes: LicenseType[]): Observable<string> {
    if (licenseTypes.some(licenseType => licenseType.licenseTypeId === reference)) {
      return of(reference);
    }
    const licenseType = licenseTypes.find(licenseType => licenseType.licenseTypeId.includes(reference));
    if (licenseType) {
      return of(licenseType.licenseTypeId);
    } else {
      return throwError(() => new InvalidStateError(`There is no license type with the id ${reference}.`));
    }
  }

  @Action(SelectLicenseValidity)
  selectLicenseValidity(ctx: StateContext<ShoppingCartStateModel>, { validity }: SelectLicenseValidity): void {
    ctx.patchState({ selectedLicenseValidity: validity });
  }

  @Action(RequestRefreshPricing)
  requestPricingInformationUpdate(ctx: StateContext<ShoppingCartStateModel>): Observable<unknown> {
    return combineLatest([
      this.store.select(ShoppingCartState.selectedLicenseTypeId),
      this.store.select(ShoppingCartState.selectedQuantity),
      this.pricingRelevantBillingInfos$
    ]).pipe(
      take(1),
      switchMap(([licenseTypeId, quantity, { countryCode, billingInformationId: billingAddressId }]) =>
        ctx.dispatch(new RefreshPricing(licenseTypeId, countryCode, billingAddressId, quantity))
      )
    );
  }

  @Action(RefreshPricing)
  updatePricingInformation(
    ctx: StateContext<ShoppingCartStateModel>,
    { licenseTypeId, countryCode, billingAddressId, quantity }: RefreshPricing
  ): Observable<unknown> {
    this.refreshPricingRequested.next();
    ctx.patchState({ pricingInformation: 'LOADING' });
    return this.fetchPricingInformation(licenseTypeId, countryCode, billingAddressId, quantity).pipe(
      tap({
        next: pricingInformation => ctx.patchState({ pricingInformation }),
        error: () => ctx.patchState({ pricingInformation: null })
      }),
      tapNextOrError(() => ctx.dispatch(new ClearNotifications(ShoppingCartState.PRICING_TAG))),
      takeUntil(this.refreshPricingRequested),
      handleErrors(MARKETPLACE_ERROR_HANDLERS, {
        INVALID_VAT_ID: new ShowErrorNotification(MSG_CHECKOUT_PRICING_INVALID_VAT_ID, { tags: [ShoppingCartState.PRICING_TAG] }),
        VAT_SERVICE_NOT_REACHABLE: new ShowErrorNotification(MSG_CHECKOUT_PRICING_VAT_SERVICE_NOT_REACHABLE, { tags: [ShoppingCartState.PRICING_TAG] }),
        COUNTRY_INVALID: new ShowErrorNotification(MSG_CHECKOUT_PRICING_COUNTRY_INVALID, { tags: [ShoppingCartState.PRICING_TAG] }),
        INTERNAL_VAT_CALCULATION_ERROR: new ShowErrorNotification(MSG_CHECKOUT_PRICING_INTERNAL_VAT_CALCULATION_ERROR, {
          tags: [ShoppingCartState.PRICING_TAG]
        }),
        QUANTITY_CONSTRAINTS_VIOLATION: [
          new ShowErrorNotification(MSG_CHECKOUT_PRICING_QUANTITY_CONSTRAINTS_VIOLATION, { tags: [ShoppingCartState.PRICING_TAG] }),
          new GotoError({ clearNotifications: false, queryParams: { errorType: 'QUANTITY_CONSTRAINTS_VIOLATION' } })
        ],
        DEFAULT: new ShowErrorNotification(MSG_CHECKOUT_PRICING_DEFAULT, { tags: [ShoppingCartState.PRICING_TAG] })
      })
    );
  }

  private checkLicenseValidity(): OperatorFunction<unknown, unknown> {
    return pipe(
      switchMap(() => this.store.selectOnce(ShoppingCartState.selectedLicenseType)),
      switchMap(selectedLicenseType => this.licenseValidityWarningService.checkValidity(selectedLicenseType))
    );
  }

  private fetchPricingInformation(
    licenseTypeId: string | null,
    countryCode: string | null,
    billingAddressId: number | null,
    quantity: number | null
  ): Observable<PricingInformation | null> {
    if (licenseTypeId === null || countryCode === null || quantity === null) {
      return of(null);
    }

    const params = {
      licenseTypeId,
      userCountryIso2: countryCode,
      billingInformationId: billingAddressId ?? undefined,
      quantity
    };
    return this.http.post<PricingInformation>(PLATFORM_ENVIRONMENT.apiUrl + '/purchase/priceCalculation', params);
  }

  @Action(Purchase)
  purchase(ctx: StateContext<ShoppingCartStateModel>, action: Purchase): Observable<unknown> {
    return this.purchaseOrRenew(ctx, action, null, PLATFORM_ENVIRONMENT.apiUrl + '/purchase', {
      ALREADY_HAVE_ACTIVE_SUBSCRIPTION: new RefreshWindow(MSG_PURCHASE_ALREADY_HAVE_ACTIVE_SUBSCRIPTION),
      ALREADY_HAVE_CANCELED_SUBSCRIPTION: new RefreshWindow(MSG_PURCHASE_ALREADY_HAVE_CANCELED_SUBSCRIPTION)
    });
  }

  @Action(Renew)
  renew(ctx: StateContext<ShoppingCartStateModel>, action: Renew): Observable<unknown> {
    return this.purchaseOrRenew(ctx, action, action.contractId, PLATFORM_ENVIRONMENT.apiUrl + '/purchase/renew', {
      ALREADY_HAVE_ACTIVE_SUBSCRIPTION: new RefreshWindow(MSG_RENEW_ALREADY_HAVE_ACTIVE_SUBSCRIPTION),
      SUBSCRIPTION_EXPIRED: new RefreshWindow(MSG_RENEW_SUBSCRIPTION_EXPIRED)
    });
  }

  private purchaseOrRenew(
    ctx: StateContext<ShoppingCartStateModel>,
    action: PurchaseOrRenew,
    renewalContractId: string | null,
    url: string,
    errorHandlers: Record<string, unknown>
  ): Observable<unknown> {
    const actionParams = action.purchaseOrRenewActionParams;
    return this.deviceFingerprintService.deviceFingerprintId$.pipe(
      map(deviceFingerprintId => this.buildPurchaseOrRenewParams(actionParams, deviceFingerprintId)),
      map(params => (renewalContractId ? { ...params, contractId: renewalContractId } : params)),
      switchMap(params => this.http.post<PaymentMethodRedirection | null>(url, params)),
      switchMap(redirection =>
        actionParams.paymentInformation !== null
          ? this.handleScaRedirectionIfNecessary(actionParams.paymentInformation, renewalContractId ? 'renew' : 'purchase', redirection)
          : of(null)
      ),
      handleErrors(MARKETPLACE_ERROR_HANDLERS, {
        AUTHORIZATION_REFUSED: new ShowErrorNotification(MSG_PURCHASE_OR_RENEW_AUTHORIZATION_REFUSED),
        BILLING_INFORMATION_NOT_FOUND: new RefreshWindow(MSG_PURCHASE_OR_RENEW_BILLING_INFORMATION_NOT_FOUND),
        BILLING_INFORMATION_REQUIRED_MISSING: new ShowErrorNotification(MSG_PURCHASE_OR_RENEW_BILLING_INFORMATION_REQUIRED_MISSING),
        BILLING_INFORMATION_REQUIRED_NOT_SELECTED: new ShowErrorNotification(MSG_PURCHASE_OR_RENEW_BILLING_INFORMATION_REQUIRED_NOT_SELECTED),
        CANCELED_BY_USER: () => undefined,
        CARD_EXPIRED: new ShowErrorNotification(MSG_PURCHASE_OR_RENEW_CARD_EXPIRED),
        COUNTRY_INVALID: new ShowErrorNotification(MSG_PURCHASE_OR_RENEW_COUNTRY_INVALID),
        CVC_DECLINED: new ShowErrorNotification(MSG_PURCHASE_OR_RENEW_CVC_DECLINED),
        ENCRYPTED_DATA_EXPIRED: new ShowErrorNotification(MSG_PURCHASE_OR_RENEW_ENCRYPTED_DATA_EXPIRED),
        EXPIRY_DATE_INVALID: new ShowErrorNotification(MSG_PURCHASE_OR_RENEW_EXPIRY_DATE_INVALID),
        INTERNAL_VAT_CALCULATION_ERROR: new ShowErrorNotification(MSG_PURCHASE_OR_RENEW_INTERNAL_VAT_CALCULATION_ERROR),
        INVALID_CARD_NUMBER: new ShowErrorNotification(MSG_PURCHASE_OR_RENEW_INVALID_CARD_NUMBER),
        INVALID_ENCRYPTED_DATA: new ShowErrorNotification(MSG_PURCHASE_OR_RENEW_INVALID_ENCRYPTED_DATA),
        INVALID_IBAN: new ShowErrorNotification(MSG_PURCHASE_OR_RENEW_INVALID_IBAN),
        FRAUD_DETECTED: new ShowErrorNotification(MSG_PURCHASE_OR_RENEW_FRAUD_DETECTED),
        INVALID_VAT_ID: new ShowErrorNotification(MSG_PURCHASE_OR_RENEW_INVALID_VAT_ID),
        METADATA_ALREADY_USED: new ShowErrorNotification(
          MSG_PURCHASE_OR_RENEW_METADATA_ALREADY_USED(actionParams.licenseType.kind.type === 'SUBSCRIPTION' ? 'subscription' : 'purchase')
        ),
        NOT_ENOUGH_BALANCE: new ShowErrorNotification(MSG_PURCHASE_OR_RENEW_NOT_ENOUGH_BALANCE),
        NOT_ENOUGH_BUDGET: new ShowErrorNotification(MSG_PURCHASE_OR_RENEW_NOT_ENOUGH_BUDGET),
        OUTDATED_BILLING_INFORMATION_VERSION: new RefreshWindow(MSG_PURCHASE_OR_RENEW_OUTDATED_BILLING_INFORMATION_VERSION),
        OUTDATED_PAYMENT_INFORMATION_VERSION: new RefreshWindow(MSG_PURCHASE_OR_RENEW_OUTDATED_PAYMENT_INFORMATION_VERSION),
        PAYMENT_METHOD_NOT_FOUND: new RefreshWindow(MSG_PURCHASE_OR_RENEW_PAYMENT_METHOD_NOT_FOUND),
        PAYPAL_PAYMENT_REFUSED: new ShowErrorNotification(MSG_PURCHASE_OR_RENEW_PAYPAL_PAYMENT_REFUSED),
        PAYMENT_SUCCESS_BUT_ERROR: [
          new ShowErrorNotification(MSG_PURCHASE_OR_RENEW_PAYMENT_SUCCESS_BUT_ERROR),
          new GotoError({ clearNotifications: false, queryParams: { errorType: 'PAYMENT_SUCCESS_BUT_ERROR' } })
        ],
        QUANTITY_CONSTRAINTS_VIOLATION: [
          new ShowErrorNotification(MSG_PURCHASE_OR_RENEW_QUANTITY_CONSTRAINTS_VIOLATION),
          new GotoError({ clearNotifications: false, queryParams: { errorType: 'QUANTITY_CONSTRAINTS_VIOLATION' } })
        ],
        REDIRECT_ID_NOT_KNOWN: new ShowErrorNotification(MSG_PURCHASE_OR_RENEW_REDIRECT_ID_NOT_KNOWN),
        REJECTED_PAYMENT: new ShowErrorNotification(MSG_PURCHASE_OR_RENEW_REJECTED_PAYMENT),
        VAT_SERVICE_NOT_REACHABLE: new ShowErrorNotification(MSG_PURCHASE_OR_RENEW_VAT_SERVICE_NOT_REACHABLE),
        WRONG_PRICING: new RefreshWindow(MSG_PURCHASE_OR_RENEW_WRONG_PRICING),
        DEFAULT:
          actionParams.licenseType.kind.type === 'SUBSCRIPTION'
            ? new ShowErrorNotification(MSG_PURCHASE_OR_RENEW_SUBSCRIPTION_DEFAULT)
            : new ShowErrorNotification(MSG_PURCHASE_OR_RENEW_DEFAULT),
        ...LINK_ACCOUNT_ERROR_HANDLERS(this.store),
        ...errorHandlers
      }),
      switchMap(() => ctx.dispatch(new FetchAssets()))
    );
  }

  private buildPurchaseOrRenewParams(actionParams: PurchaseOrRenewActionParams, deviceFingerprintId: string | null): PurchaseOrRenewParameters {
    const paymentInfo = actionParams.paymentInformation;
    const browserInfo = this.adyenBrowserInfoService.getBrowserInfo();

    return {
      additions: this.getSanitizedAdditions(actionParams.additions),
      paymentInformation: isReusablePaymentMethodIdentifier(paymentInfo)
        ? { id: paymentInfo.id, serversideVersion: paymentInfo.serversideVersion }
        : actionParams.paymentInformation,
      billingInformation: actionParams.billingAddress && actionParams.billingAddress.id,
      billingInformationServersideVersion: actionParams.billingAddress && actionParams.billingAddress.serversideVersion,
      country: actionParams.country,
      license: actionParams.licenseType.licenseTypeId,
      currency: actionParams.pricingInformation.currency,
      netValue: actionParams.pricingInformation.netValue,
      total: actionParams.pricingInformation.total,
      quantity: actionParams.quantity,
      linkedAccountId: this.store.selectSnapshot(LinkAccountState.accountId),
      deviceFingerprint: deviceFingerprintId,
      browserInfo,
      validity: this.serializedProductService.serializeValidityContext(actionParams.validity)
    };
  }

  private getSanitizedAdditions(additions: PurchaseOrRenewAddition[]): PurchaseOrRenewAddition<string>[] {
    return additions.map(addition => ({
      type: 'PERSONALIZATION',
      persons: addition.persons.map(person => ({ ...person, birthDate: person.birthDate?.toISOString().slice(0, 10) ?? null }))
    }));
  }

  private handleScaRedirectionIfNecessary(
    paymentInformation: PaymentInformation,
    reason: 'purchase' | 'renew',
    redirection: PaymentMethodRedirection | null
  ): Observable<unknown> {
    if (redirection) {
      const creditCardType = this.getCreditCardTypeForSca(paymentInformation);
      return this.scaService.performSca(redirection, creditCardType, reason);
    }
    return of(null);
  }

  private getCreditCardTypeForSca(paymentInformation: PaymentInformation): CreditCardType {
    if (paymentInformation.type === 'CREDIT_CARD') {
      return paymentInformation.cardType;
    }
    if (paymentInformation.type === 'GOOGLE_PAY') {
      return this.walletPaymentDataService.mapGooglePayPaymentDataToCreditCardType(paymentInformation);
    }
    throw new InvalidStateError(`SCA requested for payment method of type ${paymentInformation.type}`);
  }

  @Action(DownloadLatestInvoice)
  downloadLatestInvoice(ctx: StateContext<ShoppingCartStateModel>, { licenceTypeId, paymentType }: DownloadLatestInvoice): Observable<unknown> {
    return this.http
      .get(PLATFORM_ENVIRONMENT.apiUrl + '/purchase/invoice/download_latest?license=' + licenceTypeId + '&paymentType=' + paymentType, {
        observe: 'response',
        responseType: 'blob'
      })
      .pipe(
        tap(response => this.downloadService.downloadFileFromResponse(response)),
        handleErrors(MARKETPLACE_ERROR_HANDLERS, {
          DEFAULT: new ShowErrorNotification(MSG_DOWNLOAD_LATEST_INVOICE_DEFAULT)
        })
      );
  }
}
